import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { plans } from '../data/plans';
import { API_CONFIG } from '../utils/constants';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import StripeCheckoutModal from '../components/modals/StripeCheckoutModal';
import { SubscriptionSuccessModal } from '../components/modals/SubscriptionSuccessModal';
import { PaymentFailureModal } from '../components/modals/PaymentFailureModal';
import { getSubscriptionPlans, SubscriptionPlan } from '../services/credits';
import { useTracking } from '../hooks/useTracking';
import { IS_PRODUCTION, IS_DEVELOPMENT } from '../config/environment';
import { cacheManager, CACHE_KEYS } from '../services/cacheManager';
import PromotionalBanner from '../components/PromotionalBanner';
import PlanCardWithExpressCheckout, { PlanSetupIntent } from '../components/PlanCardWithExpressCheckout';

export default function UpgradePage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { openAuthModal, isOpen: isAuthModalOpen, closeAuthModal } = useAuthModal();
  const { track } = useTracking();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const pageLoadTimeRef = useRef<number>(Date.now());

  // Stripe key validation function
  const validateStripeKey = (publishableKey: string): boolean => {
    const isLiveKey = publishableKey.startsWith('pk_live_');
    const isTestKey = publishableKey.startsWith('pk_test_');
    
    // Valid combinations:
    // 1. Production environment + Live key ✅
    // 2. Development environment + Test key ✅
    
    // Invalid combinations:
    // 3. Production environment + Test key ⚠️ (warn but allow)
    // 4. Development environment + Live key ❌ (error)
    
    if (IS_DEVELOPMENT && isLiveKey) {
      console.error('[Stripe] ❌ CRITICAL: Using LIVE Stripe key in development!');
      console.error('[Stripe] This could cause real charges. Please use test keys in dev.');
      return false;
    }
    
    if (IS_PRODUCTION && isTestKey) {
      console.warn('[Stripe] ⚠️ WARNING: Using TEST Stripe key in production.');
      console.warn('[Stripe] Real payments will fail. Please configure live keys.');
      // Allow but warn
    }
    
    if (IS_PRODUCTION && isLiveKey) {
      // Correct configuration
    }
    
    if (IS_DEVELOPMENT && isTestKey) {
      // Correct configuration
    }
    
    return true;
  };
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Always use monthly - toggle is hidden but kept for compatibility
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [pendingPlan, setPendingPlan] = useState<string | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [selectedPlanId, setSelectedPlanIdState] = useState<string | null>(null);
  const [stripePublishableKey, setStripePublishableKey] = useState<string | null>(null);
  
  // Use regular state management instead of useOptimisticData to avoid Suspense
  const [stripePlansResponse, setStripePlansResponse] = useState<{ plans: SubscriptionPlan[] } | null>(null);
  const [loadingPlans, setLoadingPlans] = useState(false);

  // Convert plans array to map for easy lookup
  const stripePlans: Record<string, SubscriptionPlan> = React.useMemo(() => {
    if (!stripePlansResponse) return {};
    const plansMap: Record<string, SubscriptionPlan> = {};
    stripePlansResponse.plans.forEach((plan) => {
      plansMap[plan.id] = plan;
    });
    return plansMap;
  }, [stripePlansResponse]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPaymentFailureModal, setShowPaymentFailureModal] = useState(false);

  // Express Checkout pre-loading state
  const [planSetupIntents, setPlanSetupIntents] = useState<Record<string, PlanSetupIntent>>({
    basic: { clientSecret: '', customerId: '', status: 'loading' },
    pro: { clientSecret: '', customerId: '', status: 'loading' },
    premium: { clientSecret: '', customerId: '', status: 'loading' },
  });
  const [expressStripePromise, setExpressStripePromise] = useState<Promise<Stripe | null> | null>(null);

  // CRITICAL: Clear stale OAuth flags to prevent random redirects from /upgrade
  // This is a revenue-protection measure - users on checkout must not be redirected
  useEffect(() => {
    if (isAuthenticated) {
      try {
        sessionStorage.removeItem('oauth_initiated_time');
        sessionStorage.removeItem('oauth_provider');
        console.log('[UpgradePage] Cleared stale OAuth flags to prevent redirect');
      } catch (e) {
        console.error('[UpgradePage] Failed to clear OAuth flags:', e);
      }
    }
  }, [isAuthenticated]);

  // Load subscription plans without Suspense (prevents error #426)
  useEffect(() => {
    // Load from cache first (synchronous, immediate)
    const cached = cacheManager.get<{ plans: SubscriptionPlan[] }>(CACHE_KEYS.SUBSCRIPTION_PLANS);
    if (cached) {
      console.log('[UpgradePage] Using cached subscription plans');
      setStripePlansResponse(cached);
    }
    
    // Then fetch fresh data in background (async, non-blocking)
    setLoadingPlans(true);
    getSubscriptionPlans()
      .then(response => {
        console.log('[UpgradePage] Fetched fresh subscription plans');
        setStripePlansResponse(response);
        // Cache will be updated by the API service
      })
      .catch(error => {
        console.error('[UpgradePage] Failed to load subscription plans:', error);
        // Don't create fake fallback plans - could break Stripe payments
        // If no cached data, user will see loading state or default plan cards
      })
      .finally(() => setLoadingPlans(false));
  }, []);

  // Detect back navigation and force immediate cached data (prevents Suspense on back nav)
  useEffect(() => {
    const handlePopState = () => {
      console.log('[UpgradePage] Back navigation detected, loading cached data immediately');
      const cached = cacheManager.get<{ plans: SubscriptionPlan[] }>(CACHE_KEYS.SUBSCRIPTION_PLANS);
      if (cached) {
        setStripePlansResponse(cached);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Pre-load SetupIntents for Express Checkout on all plan cards
  useEffect(() => {
    // Only pre-load for authenticated users with email
    if (!isAuthenticated || !user?.email || !user?.id) {
      // Reset to loading state if user logs out
      setPlanSetupIntents({
        basic: { clientSecret: '', customerId: '', status: 'loading' },
        pro: { clientSecret: '', customerId: '', status: 'loading' },
        premium: { clientSecret: '', customerId: '', status: 'loading' },
      });
      return;
    }

    console.log('[UpgradePage] Pre-loading SetupIntents for Express Checkout');

    // Initialize Stripe
    const initStripe = async () => {
      try {
        // Fetch publishable key from backend
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/stripe/config`);
        const data = await response.json();
        if (data.success && data.publishable_key) {
          // Validate key
          if (validateStripeKey(data.publishable_key)) {
            const stripePromise = loadStripe(data.publishable_key);
            setExpressStripePromise(stripePromise);
          }
        }
      } catch (err) {
        console.error('[UpgradePage] Failed to init Stripe for Express Checkout:', err);
      }
    };

    initStripe();

    // Parallel fetch all 3 SetupIntents
    const planIds = ['basic', 'pro', 'premium'] as const;

    planIds.forEach(async (planId) => {
      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/stripe/create-setup-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            plan_id: planId,
            billing_period: 'monthly',
            email: user.email,
            user_id: user.id,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to create setup intent for ${planId}`);
        }

        const data = await response.json();

        if (data.success && data.client_secret && data.customer_id) {
          setPlanSetupIntents(prev => ({
            ...prev,
            [planId]: {
              clientSecret: data.client_secret,
              customerId: data.customer_id,
              status: 'ready' as const,
            },
          }));
          console.log(`[UpgradePage] SetupIntent ready for ${planId}`);
        } else {
          throw new Error('Invalid response from create-setup-intent');
        }
      } catch (err) {
        console.error(`[UpgradePage] Failed to pre-load SetupIntent for ${planId}:`, err);
        setPlanSetupIntents(prev => ({
          ...prev,
          [planId]: {
            clientSecret: '',
            customerId: '',
            status: 'error' as const,
          },
        }));
      }
    });

    // Track pre-loading started
    track('express_checkout_preload_started', {
      user_id: user.id,
      plan_count: planIds.length,
    });
  }, [isAuthenticated, user?.email, user?.id, track]);

  // Refactored subscribe function that takes planId as parameter
  const handleSubscribeWithPlan = useCallback(async (planId?: string) => {
    const planToUse = planId || selectedPlan;
    
    if (!planToUse) {
      setError('Please select a plan first');
      return;
    }

    if (!user) {
      setError('Please sign in to subscribe');
      return;
    }

    // Check if user has email (required for Stripe)
    if (!user.email) {
      setError('Please sign in with an email address to subscribe. Anonymous users cannot subscribe.');
      return;
    }

    setIsSubscribing(true);
    setError(null);

    try {
      const apiBaseUrl = API_CONFIG.BASE_URL;

      // Plan IDs match backend (basic, pro, premium)
      const planIdToUse = planToUse;
      
      // Validate plan ID
      const validPlans = ['basic', 'pro', 'premium'];
      if (!validPlans.includes(planIdToUse)) {
        throw new Error(`Invalid plan: ${planIdToUse}. Please contact support.`);
      }

      // Create SetupIntent for PaymentElement (native form fields)
      const response = await fetch(`${apiBaseUrl}/api/stripe/create-setup-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_id: planIdToUse,
          billing_period: 'monthly', // Always monthly - toggle is hidden
          email: user.email,
          user_id: user.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Backend error:', errorData);
        throw new Error(errorData.error || `Failed to create setup intent (${response.status})`);
      }

      const data = await response.json();
      
      if (data.success && data.client_secret && data.customer_id) {
        // Store setup intent and customer info for PaymentElement
        setCheckoutClientSecret(data.client_secret);
        setCustomerId(data.customer_id);
        setSelectedPlanIdState(planIdToUse);
        setSelectedPlan(planIdToUse);
        
        // Store publishable key from backend response (if provided)
        if (data.publishable_key) {
          const receivedKey = data.publishable_key;
          
          // Validate the Stripe key against the environment
          if (!validateStripeKey(receivedKey)) {
            setError('Stripe configuration error: Invalid key for this environment. Please contact support.');
            setIsSubscribing(false);
            return;
          }
          
          setStripePublishableKey(receivedKey);
        }
        
        setShowCheckout(true);
        
        // Track begin_checkout event
        const plan = plans.find((p) => p.id === planIdToUse);
        const stripePlan = stripePlans[planIdToUse];
        // Always use monthly price (toggle is hidden, always monthly)
        const monthlyEquivalentPrice = stripePlan?.price_monthly_usd || 0;
        
        track('begin_checkout', {
          plan_id: planIdToUse,
          plan_name: plan?.name || planIdToUse,
          plan_price: monthlyEquivalentPrice,
          currency: 'USD',
          value: monthlyEquivalentPrice, // Always monthly equivalent
        });
      } else {
        throw new Error('No setup intent client secret returned');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process subscription');
      setIsSubscribing(false);
    }
  }, [billingPeriod, user, selectedPlan, stripePlans, track]);

  // Handle auth success - proceed with pending plan if exists
  useEffect(() => {
    if (isAuthenticated && pendingPlan && user) {
      // User just authenticated, proceed with the pending plan
      setSelectedPlan(pendingPlan);
      const planToProcess = pendingPlan;
      setPendingPlan(null);
      // Trigger subscribe flow
      handleSubscribeWithPlan(planToProcess);
    }
  }, [isAuthenticated, pendingPlan, user, handleSubscribeWithPlan]);

  // Clear pending plan if auth modal closes without authentication
  useEffect(() => {
    if (!isAuthModalOpen && !isAuthenticated && pendingPlan) {
      // Modal closed without auth, clear pending plan
      setPendingPlan(null);
    }
  }, [isAuthModalOpen, isAuthenticated, pendingPlan]);

  // OPTION A: Prevent auth modal from causing redirects for already-authenticated users
  // Close auth modal immediately if user is authenticated (prevents stale OAuth state issues)
  useEffect(() => {
    if (isAuthenticated && isAuthModalOpen) {
      console.log('[UpgradePage] 🛡️ Closing auth modal for already-authenticated user (preventing auto-redirect)');
      track('upgrade_page_auth_modal_closed_for_authenticated', {
        user_id: user?.id,
        time_on_page_ms: Date.now() - pageLoadTimeRef.current,
      });
      closeAuthModal();
    }
  }, [isAuthenticated, isAuthModalOpen, closeAuthModal, track, user?.id]);

  // FIX 3: Clear lander-1 modal state when /upgrade page mounts (defensive cleanup)
  // This prevents processing modals from incorrectly appearing on /upgrade
  useEffect(() => {
    const lander1ModalState = localStorage.getItem('genanything_lander1_modal_state');
    if (lander1ModalState) {
      console.log('[UpgradePage] 🧹 Clearing stale lander-1 modal state on mount');
      localStorage.removeItem('genanything_lander1_modal_state');
    }
  }, []); // Run once on mount

  // FIX 4: Clear stale OAuth flags for authenticated users (prevents OAuth loops)
  // These flags shouldn't exist if user is already authenticated on /upgrade
  useEffect(() => {
    if (isAuthenticated) {
      const oauthInitiated = sessionStorage.getItem('genanything_oauth_initiated');
      const oauthProvider = sessionStorage.getItem('genanything_oauth_provider');
      
      if (oauthInitiated || oauthProvider) {
        console.log('[UpgradePage] 🧹 Clearing stale OAuth flags for authenticated user');
        sessionStorage.removeItem('genanything_oauth_initiated');
        sessionStorage.removeItem('genanything_oauth_provider');
        
        track('upgrade_page_oauth_flags_cleared', {
          user_id: user?.id,
          had_oauth_initiated: !!oauthInitiated,
          had_oauth_provider: !!oauthProvider,
        });
      }
    }
  }, [isAuthenticated, track, user?.id]);

  // Track view_content when page loads - global deduplication in useTracking will prevent duplicates
  useEffect(() => {
    track('view_content', {
      content_type: 'upgrade_page',
      page_path: '/upgrade',
    });
  }, [track]);

  // Track page unload and time on page
  useEffect(() => {
    const handleBeforeUnload = () => {
      const timeOnPage = Date.now() - pageLoadTimeRef.current;
      track('upgrade_page_unload', {
        time_on_page_ms: timeOnPage,
        user_id: user?.id,
        is_authenticated: isAuthenticated,
      });
    };

    // Track on unmount (navigation away)
    return () => {
      const timeOnPage = Date.now() - pageLoadTimeRef.current;
      track('upgrade_page_exit', {
        time_on_page_ms: timeOnPage,
        user_id: user?.id,
        is_authenticated: isAuthenticated,
      });
    };
  }, [track, user?.id, isAuthenticated]);

  // Track Facebook browser back button/gestures
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      const timeOnPage = Date.now() - pageLoadTimeRef.current;
      const isFacebookBrowser = /FBAN|FBAV/i.test(navigator.userAgent);
      
      console.log('[UpgradePage] Browser back button detected', {
        timeOnPage,
        isFacebookBrowser,
      });
      
      track('upgrade_page_back_button', {
        time_on_page_ms: timeOnPage,
        is_facebook_browser: isFacebookBrowser,
        is_mobile: /Android|iPhone|iPad/i.test(navigator.userAgent),
        user_agent: navigator.userAgent,
        user_id: user?.id,
      });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [track, user?.id]);

  // Plans are now loaded via useOptimisticData hook above
  // No need for manual fetch effect

  // New function to handle plan selection with auth check
  const handlePlanClick = async (planId: string) => {
    // Track add_to_cart event
    const plan = plans.find((p) => p.id === planId);
    const stripePlan = stripePlans[planId];
    // Always use monthly price (toggle is hidden, always monthly)
    const monthlyEquivalentPrice = stripePlan?.price_monthly_usd || 0;
    
    track('add_to_cart', {
      plan_id: planId,
      plan_name: plan?.name || planId,
      plan_price: monthlyEquivalentPrice,
      currency: 'USD',
      value: monthlyEquivalentPrice, // Always monthly equivalent
    });

    // If not authenticated, open auth modal and store pending plan
    if (!isAuthenticated || !user) {
      setPendingPlan(planId);
      openAuthModal({
        afterAuthRedirect: '/upgrade',
        reason: 'select-plan',
        fromPath: '/upgrade',
        onCancelBehavior: { type: 'stay' },
        postAuthAction: () => {
          // This will be handled by the useEffect above
        },
      });
      return;
    }

    // If authenticated, proceed directly
    setSelectedPlan(planId);
    await handleSubscribeWithPlan(planId);
  };

  return (
    <div className="min-h-screen bg-bg">
      <div className="fluid-container py-8">
        {/* Back Link */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 text-white/60 hover:text-white/80 transition-colors mb-6 group"
        >
          <svg
            className="w-3.5 h-3.5 transform group-hover:-translate-x-0.5 transition-transform"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          <span className="text-sm">Back</span>
        </button>

        {/* Valentine's Day Sale Title */}
        <h1 
          className="text-center text-white font-bold mb-6"
          style={{
            fontSize: 'clamp(1.5rem, 5vw + 0.5rem, 3rem)',
            lineHeight: '1.2'
          }}
        >
          Valentine's Day Sale 💖
        </h1>

        {/* Promotional Banner */}
        <PromotionalBanner />

        {/* Global Error Banner */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-6 py-4 rounded-lg mb-6">
            <div className="flex items-center justify-between">
              <p>{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Plan Cards Section */}
        <div className="text-center mb-16">
          {/* Plan Cards - Stacked on mobile, Grid on desktop */}
          {/* Mobile Stacked Layout (below lg) */}
          <div className="lg:hidden space-y-6 mb-16">
            {plans.map((plan) => (
              <PlanCardWithExpressCheckout
                key={`mobile-${plan.id}-${billingPeriod}`}
                plan={plan}
                stripePlan={stripePlans[plan.id]}
                setupIntent={planSetupIntents[plan.id]}
                stripePromise={expressStripePromise}
                userId={user?.id}
                isSelected={selectedPlan === plan.id}
                isSubscribing={isSubscribing}
                loadingPlans={loadingPlans}
                onFallbackClick={() => handlePlanClick(plan.id)}
                onSuccess={() => setShowSuccessModal(true)}
                onError={() => setShowPaymentFailureModal(true)}
              />
            ))}
          </div>

          {/* Desktop Grid (lg and above) */}
          <div className="hidden lg:grid grid-cols-3 gap-6 mb-16 items-stretch">
            {plans.map((plan) => (
              <PlanCardWithExpressCheckout
                key={`desktop-${plan.id}-${billingPeriod}`}
                plan={plan}
                stripePlan={stripePlans[plan.id]}
                setupIntent={planSetupIntents[plan.id]}
                stripePromise={expressStripePromise}
                userId={user?.id}
                isSelected={selectedPlan === plan.id}
                isSubscribing={isSubscribing}
                loadingPlans={loadingPlans}
                onFallbackClick={() => handlePlanClick(plan.id)}
                onSuccess={() => setShowSuccessModal(true)}
                onError={() => setShowPaymentFailureModal(true)}
              />
            ))}
          </div>
        </div>

        {/* Stripe Checkout Modal */}
        <StripeCheckoutModal
          open={showCheckout && !!checkoutClientSecret}
          onClose={() => {
            setShowCheckout(false);
            setCheckoutClientSecret(null);
            setCustomerId(null);
            setSelectedPlan(null);
            setSelectedPlanIdState(null);
            setStripePublishableKey(null);
            setIsSubscribing(false);
          }}
          clientSecret={checkoutClientSecret}
          customerId={customerId}
          planId={selectedPlanId}
          billingPeriod={billingPeriod}
          publishableKey={stripePublishableKey}
          userId={user?.id}
          onComplete={() => {
            setShowCheckout(false);
            setCheckoutClientSecret(null);
            setCustomerId(null);
            setSelectedPlan(null);
            setSelectedPlanIdState(null);
            setIsSubscribing(false);
            setShowSuccessModal(true);
          }}
          onError={(error) => {
            console.error('Checkout error:', error);
            // Show a dedicated payment failure modal for card declines or payment failures.
            setShowCheckout(false);
            setCheckoutClientSecret(null);
            setCustomerId(null);
            setSelectedPlan(null);
            setSelectedPlanIdState(null);
            setStripePublishableKey(null);
            setShowPaymentFailureModal(true);
            setIsSubscribing(false);
          }}
        />
      </div>

      {/* Subscription Success Modal */}
      {showSuccessModal && (
        <SubscriptionSuccessModal
          onClose={() => {
            setShowSuccessModal(false);
            window.location.href = '/my-collection';
          }}
        />
      )}

      {/* Payment Failure Modal */}
      {showPaymentFailureModal && (
        <PaymentFailureModal
          onClose={() => {
            setShowPaymentFailureModal(false);
          }}
        />
      )}
    </div>
  );
}

